/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// react-router-dom components
import { Link } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import { Breadcrumbs as MuiBreadcrumbs } from "@mui/material";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

function Breadcrumbs({ icon, title, route, light }) {
  const formatLabel = (str) => (typeof str === "string" ? str.replace(/-/g, " ") : str);

  const toAbs = (to) => {
    if (!to) return "/";
    return to.startsWith("/") ? to : `/${to}`;
  };

  // Κανονικοποίηση στοιχείων route -> { label, to, icon }
  const normalize = (el) => {
    if (typeof el === "string") {
      const slug = el.replace(/^\//, "");
      return { label: slug, to: `/${slug}`, icon: null };
    }
    const baseLabel =
      el.name || el.label || el.title || el.key || el.route || el.path || el.to || "";
    let to = el.route || el.path || el.to || el.href || `/${baseLabel}`;
    to = toAbs(to);
    return {
      label: baseLabel,
      to,
      icon: el.icon || null,
    };
  };

  const routeArray = Array.isArray(route) ? route : [route];
  const normalized = routeArray.map(normalize);
  const crumbs = normalized.slice(0, -1);

  // Τίτλος τελευταίου breadcrumb: προτίμηση στο title, αλλιώς από το τελευταίο item
  const lastLabel = formatLabel(title || normalized[normalized.length - 1]?.label || "");

  return (
    <MDBox mr={{ xs: 0, xl: 8 }}>
      <MuiBreadcrumbs
        sx={{
          "& .MuiBreadcrumbs-separator": {
            color: ({ palette: { white, grey } }) => (light ? white.main : grey[600]),
          },
        }}
      >
        <Link to="/">
          <MDTypography
            component="span"
            variant="body2"
            color={light ? "white" : "dark"}
            opacity={light ? 0.8 : 0.5}
            sx={{ lineHeight: 0 }}
          >
            <Icon>{icon}</Icon>
          </MDTypography>
        </Link>
        {crumbs.map(({ to, label, icon: itemIcon }) => (
          <Link to={toAbs(to)} key={to}>
            <MDTypography
              component="span"
              variant="button"
              fontWeight="regular"
              textTransform="capitalize"
              color={light ? "white" : "dark"}
              opacity={light ? 0.8 : 0.5}
              sx={{ lineHeight: 0 }}
            >
              <MDBox
                component="span"
                display="inline-flex"
                alignItems="center"
                sx={{ lineHeight: 0 }}
              >
                {itemIcon && (
                  <MDBox component="span" mr={0.5} display="inline-flex" alignItems="center">
                    {itemIcon}
                  </MDBox>
                )}
                {formatLabel(label)}
              </MDBox>
            </MDTypography>
          </Link>
        ))}
        <MDTypography
          variant="button"
          fontWeight="regular"
          textTransform="capitalize"
          color={light ? "white" : "dark"}
          sx={{ lineHeight: 0 }}
        >
          {lastLabel}
        </MDTypography>
      </MuiBreadcrumbs>
      <MDTypography
        fontWeight="bold"
        textTransform="capitalize"
        variant="h6"
        color={light ? "white" : "dark"}
        noWrap
      >
        {lastLabel}
      </MDTypography>
    </MDBox>
  );
}

// Setting default values for the props of Breadcrumbs
Breadcrumbs.defaultProps = {
  light: false,
};

// Typechecking props for the Breadcrumbs
Breadcrumbs.propTypes = {
  icon: PropTypes.node.isRequired,
  title: PropTypes.string,
  route: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          key: PropTypes.string,
          name: PropTypes.string,
          label: PropTypes.string,
          title: PropTypes.string,
          route: PropTypes.string,
          path: PropTypes.string,
          to: PropTypes.string,
          href: PropTypes.string,
          icon: PropTypes.node,
        }),
      ])
    ),
  ]).isRequired,
  light: PropTypes.bool,
};

export default Breadcrumbs;
